import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/**
 * Service for managing eligibility refresh operations
 * Handles login-triggered full eligibility recalculation with progress tracking
 */

export interface RefreshStatus {
  status: 'idle' | 'running' | 'completed' | 'failed' | 'cancelled';
  startedAt?: number;
  updatedAt?: number;
  completedAt?: number;
  totalPrograms?: number;
  processedPrograms?: number;
  errors?: string[];
  error?: string;
}

export interface RefreshResult {
  success: boolean;
  programCount?: number;
  error?: string;
}

class EligibilityRefreshService {
  private startRefreshCall = httpsCallable(functions, 'startEligibilityRefresh', { timeout: 600000 }); // 10 minutes timeout
  private getStatusCall = httpsCallable(functions, 'getEligibilityRefreshStatus');
  private cancelRefreshCall = httpsCallable(functions, 'cancelEligibilityRefresh');

  /**
   * Start a full eligibility refresh for the current user
   * Only runs if user hasn't been migrated yet
   */
  async startRefresh(): Promise<RefreshResult> {
    try {
      console.log('🔄 Starting eligibility refresh...');
      const result = await this.startRefreshCall();
      return result.data as RefreshResult;
    } catch (error: any) {
      console.error('❌ Error starting eligibility refresh:', error);
      return {
        success: false,
        error: error.message || 'Failed to start refresh'
      };
    }
  }

  /**
   * Get the current refresh status for the user
   */
  async getStatus(): Promise<RefreshStatus> {
    try {
      const result = await this.getStatusCall();
      return result.data as RefreshStatus;
    } catch (error: any) {
      console.error('❌ Error getting refresh status:', error);
      return { status: 'idle' };
    }
  }

  /**
   * Cancel the current refresh job
   */
  async cancelRefresh(): Promise<{ success: boolean }> {
    try {
      const result = await this.cancelRefreshCall();
      return result.data as { success: boolean };
    } catch (error: any) {
      console.error('❌ Error cancelling refresh:', error);
      return { success: false };
    }
  }

  /**
   * Check if user needs a refresh (not migrated yet)
   */
  async needsRefresh(): Promise<boolean> {
    try {
      const status = await this.getStatus();
      
      // If there's an active job, don't start another
      if (status.status === 'running') {
        return false;
      }
      
      // Check if user has been migrated by looking for metadata
      // This is a simple check - in a real implementation, you might want
      // to call a dedicated endpoint to check migration status
      return status.status === 'idle';
    } catch (error) {
      console.error('❌ Error checking if refresh needed:', error);
      return false;
    }
  }

  /**
   * Calculate progress percentage
   */
  getProgressPercentage(status: RefreshStatus): number {
    if (!status.totalPrograms || !status.processedPrograms) {
      return 0;
    }
    return Math.round((status.processedPrograms / status.totalPrograms) * 100);
  }

  /**
   * Get user-friendly status message
   */
  getStatusMessage(status: RefreshStatus): string {
    switch (status.status) {
      case 'running':
        const progress = this.getProgressPercentage(status);
        return `Calculating eligibility... ${progress}%`;
      case 'completed':
        return 'Eligibility calculation completed!';
      case 'failed':
        return 'Eligibility calculation failed. Please try again.';
      case 'cancelled':
        return 'Eligibility calculation cancelled.';
      default:
        return '';
    }
  }

  /**
   * Check if status indicates an active operation
   */
  isActive(status: RefreshStatus): boolean {
    return status.status === 'running';
  }

  /**
   * Check if status indicates completion
   */
  isCompleted(status: RefreshStatus): boolean {
    return status.status === 'completed';
  }

  /**
   * Check if status indicates an error
   */
  hasError(status: RefreshStatus): boolean {
    return status.status === 'failed' || status.status === 'cancelled';
  }
}

export const eligibilityRefreshService = new EligibilityRefreshService();
